import { useEffect, useState } from "react";
import { getSupabase } from "../lib/supabase";
export default function Dashboard() {
  const [stats, setStats] = useState({
    totalContacts: 0,
    contactsThisWeek: 0,
    publishedTestimonials: 0,
    publishedInsights: 0,
  });

  const [activity, setActivity] = useState([]);

  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingActivity, setLoadingActivity] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  async function fetchDashboardData() {
    const supabase = await getSupabase();
    /* ================= STATS ================= */
    setLoadingStats(true);

    const [contactsTotal, contactsWeek, testimonials, insights] =
      await Promise.all([
        supabase
          .schema("api")
          .from("contact_submissions")
          .select("id", { count: "exact", head: true }),

        supabase
          .schema("api")
          .from("contact_submissions")
          .select("id", { count: "exact", head: true })
          .gte("created_at", new Date(Date.now() - 7 * 86400000).toISOString()),

        supabase
          .schema("api")
          .from("testimonials")
          .select("id", { count: "exact", head: true })
          .eq("is_published", true),

        supabase
          .schema("api")
          .from("updates")
          .select("id", { count: "exact", head: true })
          .eq("is_published", true),
      ]);

    setStats({
      totalContacts: contactsTotal.count || 0,
      contactsThisWeek: contactsWeek.count || 0,
      publishedTestimonials: testimonials.count || 0,
      publishedInsights: insights.count || 0,
    });

    setLoadingStats(false);

    /* ================= ACTIVITY ================= */
    setLoadingActivity(true);

    const [contacts, testimonialsData, insightsData] = await Promise.all([
      supabase
        .schema("api")
        .from("contact_submissions")
        .select("name, created_at")
        .order("created_at", { ascending: false })
        .limit(3),

      supabase
        .schema("api")
        .from("testimonials")
        .select("company_name, created_at")
        .order("created_at", { ascending: false })
        .limit(2),

      supabase
        .schema("api")
        .from("updates")
        .select("title, created_at")
        .order("created_at", { ascending: false })
        .limit(2),
    ]);

    const combined = [
      ...(contacts.data || []).map((c) => ({
        label: `New contact from ${c.name}`,
        time: c.created_at,
      })),
      ...(testimonialsData.data || []).map((t) => ({
        label: `Testimonial added · ${t.company_name}`,
        time: t.created_at,
      })),
      ...(insightsData.data || []).map((i) => ({
        label: `Insight created · ${i.title}`,
        time: i.created_at,
      })),
    ]
      .sort((a, b) => new Date(b.time) - new Date(a.time))
      .slice(0, 6);

    setActivity(combined);
    setLoadingActivity(false);
  }

  return (
    <div className="space-y-12">
      {/* HEADER */}
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Dashboard</h1>
        <p className="mt-1 text-sm text-slate-500">
          Operational overview & recent activity
        </p>
      </div>

      {/* METRICS */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Total Contacts"
          value={stats.totalContacts}
          loading={loadingStats}
        />
        <StatCard
          label="This Week"
          value={stats.contactsThisWeek}
          loading={loadingStats}
        />
        <StatCard
          label="Testimonials Live"
          value={stats.publishedTestimonials}
          loading={loadingStats}
        />
        <StatCard
          label="Insights Live"
          value={stats.publishedInsights}
          loading={loadingStats}
        />
      </div>

      {/* ACTIVITY */}
      <div className="rounded-2xl border border-white/20 bg-white/60 p-6 shadow-xl backdrop-blur-xl dark:bg-white/5">
        <h2 className="mb-4 text-lg font-semibold">Recent Activity</h2>

        {loadingActivity ? (
          <ul className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <li
                key={i}
                className="h-12 animate-pulse rounded-lg bg-slate-300/50 dark:bg-white/10"
              />
            ))}
          </ul>
        ) : (
          <ul className="space-y-3">
            {activity.map((a, i) => (
              <li
                key={i}
                className="flex items-center justify-between rounded-lg bg-white/70 px-4 py-3 shadow-sm dark:bg-white/10"
              >
                <span className="text-sm">{a.label}</span>
                <span className="text-xs text-slate-400">
                  {new Date(a.time).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* QUICK ACTIONS */}
      <div>
        <h2 className="mb-4 text-lg font-semibold">Quick Actions</h2>
        <div className="flex flex-wrap gap-4">
          <ActionButton href="/admin/contacts">View Contacts</ActionButton>
          <ActionButton href="/admin/testimonials">
            Add Testimonial
          </ActionButton>
          <ActionButton href="/admin/insights">Add Insight</ActionButton>
        </div>
      </div>
    </div>
  );
}

/* ================= COMPONENTS ================= */

function StatCard({ label, value, loading }) {
  return (
    <div className="relative rounded-2xl border border-white/20 bg-white/70 p-6 shadow-lg backdrop-blur-xl dark:bg-white/5">
      <p className="text-sm text-slate-500">{label}</p>

      {loading ? (
        <div className="mt-3 h-8 w-20 animate-pulse rounded bg-slate-300/60 dark:bg-white/10" />
      ) : (
        <p className="mt-2 text-3xl font-semibold tracking-tight">{value}</p>
      )}
    </div>
  );
}

function ActionButton({ href, children }) {
  return (
    <a
      href={href}
      className="rounded-xl bg-[rgb(var(--brand-primary))] px-6 py-3 text-sm font-semibold text-white shadow-md transition hover:opacity-90 hover:shadow-lg"
    >
      {children}
    </a>
  );
}
